/**
 * Moodboard freeform helpers (mirrors BACK layout logic for UI).
 * Coords: normalized 0–1, origin top-left.
 */

export const SLOT_KEYS = [
  "hero",
  "midLeft",
  "midRight",
  "botTL",
  "botTR",
  "botBL",
  "botBR",
];

export const SLOTS_PER_PAGE = 7;
export const MIN_SLOT = 0.05;
export const LEFT_COL_RATIO = 0.26;

let slotIdSeq = 0;
export function newSlotId(prefix = "s") {
  slotIdSeq += 1;
  return `${prefix}_${Date.now().toString(36)}_${slotIdSeq}`;
}

let markIdSeq = 0;
export function newMarkId(prefix = "m") {
  markIdSeq += 1;
  return `${prefix}_${Date.now().toString(36)}_${markIdSeq}`;
}

export const MARK_TYPES = ["path", "line", "arrow", "ellipse", "rect", "text"];

/** Tipografías de marcas de texto (UI + PDF estándar). */
export const MARK_FONT_FAMILIES = {
  sans: {
    id: "sans",
    css: "ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto, sans-serif",
  },
  serif: {
    id: "serif",
    css: 'Georgia, "Times New Roman", Times, serif',
  },
  mono: {
    id: "mono",
    css: "ui-monospace, SFMono-Regular, Menlo, Consolas, monospace",
  },
};

export function normalizeMarkFontFamily(raw) {
  const id = String(raw || "sans").toLowerCase();
  return MARK_FONT_FAMILIES[id] ? id : "sans";
}

export function markFontCss(family) {
  return (
    MARK_FONT_FAMILIES[normalizeMarkFontFamily(family)]?.css ||
    MARK_FONT_FAMILIES.sans.css
  );
}

export const DEFAULT_MARK_COLOR = "#e11d48";
export const DEFAULT_MARK_STROKE = 2;

function clamp01(v) {
  const n = Number(v);
  if (!Number.isFinite(n)) return 0;
  if (n < 0) return 0;
  if (n > 1) return 1;
  return n;
}

function normalizeHexColorLoose(raw, fallback = DEFAULT_MARK_COLOR) {
  const s = String(raw || "").trim();
  if (/^#[0-9a-fA-F]{6}$/.test(s)) return s.toLowerCase();
  if (/^#[0-9a-fA-F]{3}$/.test(s)) {
    return `#${s[1]}${s[1]}${s[2]}${s[2]}${s[3]}${s[3]}`.toLowerCase();
  }
  return fallback;
}

function clampMarkStroke(v) {
  const n = Number(v);
  if (!Number.isFinite(n)) return DEFAULT_MARK_STROKE;
  return Math.min(12, Math.max(0.5, Math.round(n * 10) / 10));
}

function clampMarkPoint(p) {
  if (!p || typeof p !== "object") return { x: 0, y: 0 };
  return { x: clamp01(p.x), y: clamp01(p.y) };
}

/** Normalize a page annotation mark (coords 0–1 over the canvas). */
export function clampMark(raw = {}) {
  const typeRaw = String(raw.type || "").toLowerCase();
  const type = MARK_TYPES.includes(typeRaw) ? typeRaw : "path";
  const color = normalizeHexColorLoose(raw.color, DEFAULT_MARK_COLOR);
  const stroke = clampMarkStroke(raw.stroke ?? raw.strokeWidth);
  const base = {
    id: String(raw.id || newMarkId()),
    type,
    color,
    stroke,
  };

  if (type === "path") {
    const pts = Array.isArray(raw.points) ? raw.points.map(clampMarkPoint) : [];
    return { ...base, points: pts.length ? pts : [{ x: 0, y: 0 }] };
  }
  if (type === "line" || type === "arrow") {
    return {
      ...base,
      x1: clamp01(raw.x1 ?? 0),
      y1: clamp01(raw.y1 ?? 0),
      x2: clamp01(raw.x2 ?? 0),
      y2: clamp01(raw.y2 ?? 0),
    };
  }
  if (type === "ellipse" || type === "rect") {
    let x = Number(raw.x);
    let y = Number(raw.y);
    let w = Number(raw.w);
    let h = Number(raw.h);
    if (!Number.isFinite(x)) x = 0;
    if (!Number.isFinite(y)) y = 0;
    if (!Number.isFinite(w)) w = 0;
    if (!Number.isFinite(h)) h = 0;
    if (w < 0) {
      x += w;
      w = -w;
    }
    if (h < 0) {
      y += h;
      h = -h;
    }
    x = clamp01(x);
    y = clamp01(y);
    w = Math.min(clamp01(w), 1 - x);
    h = Math.min(clamp01(h), 1 - y);
    return { ...base, x, y, w, h };
  }
  // text
  let fontSize = Number(raw.fontSize);
  if (!Number.isFinite(fontSize)) fontSize = 14;
  fontSize = Math.min(72, Math.max(8, Math.round(fontSize)));
  return {
    ...base,
    x: clamp01(raw.x ?? 0),
    y: clamp01(raw.y ?? 0),
    text: String(raw.text ?? "").slice(0, 200),
    fontSize,
    fontFamily: normalizeMarkFontFamily(raw.fontFamily),
  };
}

/**
 * Desplaza una marca (path/line/arrow/rect/ellipse/text) en coords normalizadas 0–1.
 */
export function translateMark(mark, dx, dy) {
  if (!mark || (!dx && !dy)) return mark;
  const ox = Number(dx) || 0;
  const oy = Number(dy) || 0;
  if (mark.type === "path") {
    return clampMark({
      ...mark,
      points: (mark.points || []).map((p) => ({
        x: (Number(p.x) || 0) + ox,
        y: (Number(p.y) || 0) + oy,
      })),
    });
  }
  if (mark.type === "line" || mark.type === "arrow") {
    return clampMark({
      ...mark,
      x1: (Number(mark.x1) || 0) + ox,
      y1: (Number(mark.y1) || 0) + oy,
      x2: (Number(mark.x2) || 0) + ox,
      y2: (Number(mark.y2) || 0) + oy,
    });
  }
  if (mark.type === "ellipse" || mark.type === "rect") {
    return clampMark({
      ...mark,
      x: (Number(mark.x) || 0) + ox,
      y: (Number(mark.y) || 0) + oy,
      w: mark.w,
      h: mark.h,
    });
  }
  if (mark.type === "text") {
    return clampMark({
      ...mark,
      x: (Number(mark.x) || 0) + ox,
      y: (Number(mark.y) || 0) + oy,
    });
  }
  return mark;
}

export function normalizeMarks(raw) {
  if (!Array.isArray(raw)) return [];
  return raw
    .map((m) => {
      try {
        return clampMark(m);
      } catch {
        return null;
      }
    })
    .filter(Boolean)
    .slice(0, 200);
}

export function clampSlot(raw = {}) {
  let w = Math.max(MIN_SLOT, clamp01(raw.w ?? 0.2));
  let h = Math.max(MIN_SLOT, clamp01(raw.h ?? 0.2));
  let x = clamp01(raw.x ?? 0);
  let y = clamp01(raw.y ?? 0);
  if (x + w > 1) x = Math.max(0, 1 - w);
  if (y + h > 1) y = Math.max(0, 1 - h);
  w = Math.min(w, 1 - x);
  h = Math.min(h, 1 - y);
  const shape =
    String(raw.shape || "rect").toLowerCase() === "circle" ? "circle" : "rect";
  const assetRaw = raw.assetId;
  const assetId =
    assetRaw == null || assetRaw === ""
      ? null
      : Number.isFinite(Number(assetRaw))
        ? Number(assetRaw)
        : null;
  let zoom = Number(raw.zoom);
  if (!Number.isFinite(zoom)) zoom = 1;
  zoom = Math.min(8, Math.max(0.25, zoom));
  let z = Number(raw.z);
  if (!Number.isFinite(z)) z = 0;
  z = Math.max(0, Math.min(9999, Math.round(z)));
  return {
    id: String(raw.id || newSlotId()),
    x,
    y,
    w: Math.max(MIN_SLOT, w),
    h: Math.max(MIN_SLOT, h),
    assetId,
    zoom: Math.round(zoom * 100) / 100,
    panX: clamp01(raw.panX ?? 0.5),
    panY: clamp01(raw.panY ?? 0.5),
    shape,
    z,
  };
}

/** Stable paint order: lower z behind; selected can still float in UI. */
export function sortSlotsByLayer(slots = []) {
  return [...(Array.isArray(slots) ? slots : [])].sort((a, b) => {
    const dz = (Number(a.z) || 0) - (Number(b.z) || 0);
    if (dz !== 0) return dz;
    return String(a.id || "").localeCompare(String(b.id || ""));
  });
}

/** Aviator preset in normalized top-left coords (matches BACK). */
export function aviatorPresetSlots(assetIds = []) {
  const leftW = LEFT_COL_RATIO;
  const restW = 1 - leftW;
  const colW = restW / 3;
  const rowH = 0.5;
  const x1 = leftW;
  const x2 = leftW + colW;
  const x3 = leftW + colW * 2;
  const rects = [
    { key: "hero", x: 0, y: 0, w: leftW, h: 1 },
    { key: "midLeft", x: x1, y: 0, w: colW, h: rowH },
    { key: "midRight", x: x1, y: rowH, w: colW, h: rowH },
    { key: "botTL", x: x2, y: 0, w: colW, h: rowH },
    { key: "botTR", x: x2, y: rowH, w: colW, h: rowH },
    { key: "botBL", x: x3, y: 0, w: colW, h: rowH },
    { key: "botBR", x: x3, y: rowH, w: colW, h: rowH },
  ];
  const ids = Array.isArray(assetIds) ? assetIds : [];
  return rects.map((r, idx) => {
    const rawId = ids[idx];
    const assetId =
      rawId == null || rawId === ""
        ? null
        : Number.isFinite(Number(rawId))
          ? Number(rawId)
          : null;
    return clampSlot({
      id: `aviator_${r.key}`,
      x: r.x,
      y: r.y,
      w: r.w,
      h: r.h,
      assetId,
    });
  });
}

export function emptyFreePage() {
  return { slots: aviatorPresetSlots(), marks: [] };
}

function isLegacySlotsObject(slots) {
  if (!slots || typeof slots !== "object" || Array.isArray(slots)) return false;
  return SLOT_KEYS.some((k) => Object.prototype.hasOwnProperty.call(slots, k));
}

export function migrateLegacyPage(page) {
  const src = page?.slots && typeof page.slots === "object" ? page.slots : {};
  const assetIds = SLOT_KEYS.map((k) => {
    const v = src[k];
    if (v == null || v === "") return null;
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
  });
  return { slots: aviatorPresetSlots(assetIds), marks: [] };
}

export function normalizeFreePage(page) {
  if (isLegacySlotsObject(page?.slots)) return migrateLegacyPage(page);
  const list = Array.isArray(page?.slots) ? page.slots : [];
  const marks = normalizeMarks(page?.marks);
  if (!list.length) return { ...emptyFreePage(), marks };
  return { slots: list.map((s) => clampSlot(s)), marks };
}

export function normalizePages(raw) {
  if (!Array.isArray(raw) || !raw.length) return [emptyFreePage()];
  return raw.map((p) => normalizeFreePage(p));
}

export function applyAviatorPresetToPage(page) {
  const current = normalizeFreePage(page);
  const assetIds = current.slots.map((s) => s.assetId);
  return { slots: aviatorPresetSlots(assetIds), marks: current.marks || [] };
}

export function listPageAssetIds(page) {
  return normalizeFreePage(page)
    .slots.map((s) => s.assetId)
    .filter((id) => id != null)
    .map(Number);
}

export function snap(v, step = 0.02) {
  return Math.round(v / step) * step;
}

export function snapSlot(slot, step = 0.02) {
  return clampSlot({
    ...slot,
    x: snap(slot.x, step),
    y: snap(slot.y, step),
    w: snap(slot.w, step),
    h: snap(slot.h, step),
  });
}

/** Same crop math as BACK PDF — box in any unit (e.g. 0–100%). */
export function coverFit(imgW, imgH, box, crop = {}) {
  const iw = Math.max(1, Number(imgW) || 1);
  const ih = Math.max(1, Number(imgH) || 1);
  let zoom = Number(crop.zoom);
  if (!Number.isFinite(zoom)) zoom = 1;
  zoom = Math.min(8, Math.max(0.25, zoom));
  const panX = clamp01(crop.panX ?? 0.5);
  const panY = clamp01(crop.panY ?? 0.5);
  const scale = Math.max(box.w / iw, box.h / ih) * zoom;
  const w = iw * scale;
  const h = ih * scale;
  return {
    x: box.x + (box.w - w) * panX,
    y: box.y + (box.h - h) * panY,
    w,
    h,
  };
}

export function splitCategoryLabel(raw) {
  const cat = String(raw || "Moodboard").trim();
  const dash = cat.search(/\s[-–—]\s/);
  if (dash < 0) return { main: cat, sub: "" };
  return {
    main: cat.slice(0, dash).trim(),
    sub: cat
      .slice(dash)
      .replace(/^\s*[-–—]\s*/, "")
      .trim(),
  };
}

export function splitStudioLine(raw) {
  const full = String(
    raw ||
      "Stage Design Studio · Irene Guijt | irene@stagedesign.es | +34 667 765 007",
  ).trim();
  const sep = full.search(/\s*[·|]\s*/);
  if (sep < 0) {
    return { studioName: full || "Stage Design Studio", contactLine: "" };
  }
  return {
    studioName: full.slice(0, sep).trim() || "Stage Design Studio",
    contactLine: full
      .slice(sep)
      .replace(/^\s*[·|]\s*/, "")
      .trim(),
  };
}

export const DEFAULT_CHROME_STYLE = {
  title: {
    barScale: 1,
    fontScale: 1,
    bg: "#383838",
    text: "#ffffff",
    subText: "#c7c7c7",
    /** top-left normalizado 0–1 sobre el lienzo; null = esquina por defecto */
    x: null,
    y: null,
    /** diagonal | rect | ribbon | tag */
    shape: "diagonal",
  },
  studio: {
    barScale: 1,
    fontScale: 1,
    bg: "#383838",
    name: "#8cd4e0",
    contact: "#ffffff",
    x: null,
    y: null,
    shape: "diagonal",
  },
};

export const CHROME_SHAPES = [
  { id: "diagonal", label: "Diagonal" },
  { id: "rect", label: "Rectángulo" },
  { id: "ribbon", label: "Cinta" },
  { id: "tag", label: "Etiqueta" },
];

export function normalizeChromeShape(raw) {
  const s = String(raw || "")
    .trim()
    .toLowerCase();
  if (s === "rect" || s === "rectangle" || s === "square") return "rect";
  if (s === "ribbon" || s === "banner" || s === "tape") return "ribbon";
  if (s === "tag" || s === "arrow" || s === "flag") return "tag";
  return "diagonal";
}

/**
 * clip-path CSS para barra título (which=title) o pie (studio).
 * slant: longitud CSS del chaflán (p.ej. "2.1cqw").
 */
export function chromeBarClipPath(shape, which, slant) {
  const s = normalizeChromeShape(shape);
  const side = which === "studio" ? "studio" : "title";
  if (s === "rect") return "none";
  if (s === "ribbon") {
    return `polygon(${slant} 0, 100% 0, calc(100% - ${slant}) 100%, 0 100%)`;
  }
  if (s === "tag") {
    if (side === "studio") {
      return `polygon(0 0, calc(100% - ${slant}) 0, 100% 50%, calc(100% - ${slant}) 100%, 0 100%)`;
    }
    return `polygon(${slant} 0, 100% 0, 100% 100%, ${slant} 100%, 0 50%)`;
  }
  // diagonal Stage
  if (side === "studio") {
    return `polygon(0 0, calc(100% - ${slant}) 0, 100% 100%, 0 100%)`;
  }
  return `polygon(0 0, 100% 0, 100% 100%, ${slant} 100%)`;
}

function clampChromeScale(v, fallback = 1) {
  const n = Number(v);
  if (!Number.isFinite(n)) return fallback;
  return Math.min(1.6, Math.max(0.7, n));
}

function clampChromePos(v) {
  if (v == null || v === "") return null;
  const n = Number(v);
  if (!Number.isFinite(n)) return null;
  return Math.min(0.95, Math.max(0, n));
}

function normalizeHexColor(raw, fallback) {
  const s = String(raw || "").trim();
  if (/^#[0-9a-fA-F]{6}$/.test(s)) return s.toLowerCase();
  if (/^#[0-9a-fA-F]{3}$/.test(s)) {
    return `#${s[1]}${s[1]}${s[2]}${s[2]}${s[3]}${s[3]}`.toLowerCase();
  }
  return fallback;
}

export function normalizeChromeStyle(raw) {
  let obj = raw;
  if (typeof raw === "string") {
    const t = raw.trim();
    if (!t) obj = null;
    else {
      try {
        obj = JSON.parse(t);
      } catch {
        obj = null;
      }
    }
  }
  const titleIn = obj?.title && typeof obj.title === "object" ? obj.title : {};
  const studioIn =
    obj?.studio && typeof obj.studio === "object" ? obj.studio : {};
  return {
    title: {
      barScale: clampChromeScale(
        titleIn.barScale ?? titleIn.scale,
        DEFAULT_CHROME_STYLE.title.barScale,
      ),
      fontScale: clampChromeScale(
        titleIn.fontScale,
        DEFAULT_CHROME_STYLE.title.fontScale,
      ),
      bg: normalizeHexColor(titleIn.bg, DEFAULT_CHROME_STYLE.title.bg),
      text: normalizeHexColor(titleIn.text, DEFAULT_CHROME_STYLE.title.text),
      subText: normalizeHexColor(
        titleIn.subText,
        DEFAULT_CHROME_STYLE.title.subText,
      ),
      x: clampChromePos(titleIn.x),
      y: clampChromePos(titleIn.y),
      shape: normalizeChromeShape(
        titleIn.shape ?? DEFAULT_CHROME_STYLE.title.shape,
      ),
    },
    studio: {
      barScale: clampChromeScale(
        studioIn.barScale ?? studioIn.scale,
        DEFAULT_CHROME_STYLE.studio.barScale,
      ),
      fontScale: clampChromeScale(
        studioIn.fontScale,
        DEFAULT_CHROME_STYLE.studio.fontScale,
      ),
      bg: normalizeHexColor(studioIn.bg, DEFAULT_CHROME_STYLE.studio.bg),
      name: normalizeHexColor(studioIn.name, DEFAULT_CHROME_STYLE.studio.name),
      contact: normalizeHexColor(
        studioIn.contact,
        DEFAULT_CHROME_STYLE.studio.contact,
      ),
      x: clampChromePos(studioIn.x),
      y: clampChromePos(studioIn.y),
      shape: normalizeChromeShape(
        studioIn.shape ?? DEFAULT_CHROME_STYLE.studio.shape,
      ),
    },
  };
}
